/**
 * NutSheetRow — one editable transaction row.
 *
 * 13 columns. Sold-unit + people/customer fields collapsed into a single
 * blue "Deal Info" cell (AutoPopBlock); trade-unit info + per-trade financials
 * collapsed into two amber cells (TradeInfoBlock + TradeFinancialsBlock).
 * Calls `onChange(patch)` on every edit so the parent owns state, and
 * `onCommit()` on blur to trigger persistence.
 */

import React, { useState } from 'react';
import { ClockIcon, TrashIcon } from '@heroicons/react/24/outline';
import { TypeSelect } from './TypeSelect';
import { DealNumberInput } from './DealNumberInput';
import { AutoPopBlock } from './AutoPopBlock';
import { TradeInfoBlock } from './TradeInfoBlock';
import { CurrencyInput } from './CurrencyInput';
import { TradeFinancialsBlock } from './TradeFinancialsBlock';
import { cellBase, numCell, textCell } from './cellStyles';
import { formatCurrency } from './formatters';
import {
  ACCOUNTING_COMMENT_MAX,
  DealLookupResponse,
  ExistingNutSheetMatch,
  NutSheetCategory,
  NutSheetRowDraft,
} from '../../types/nutSheet';

/**
 * The patch the deal-number lookup applies to a row (stock # + sold-unit +
 * people + trade descriptive fields). Exported so the page can defer/replay it
 * after the operator confirms a duplicate-deal-# warning. Financials stay
 * operator-entered. The stock # is only overwritten when the deal actually
 * carries one, so a manually typed stock # survives a stock-less deal row.
 */
export function dealAutofillPatch(result: DealLookupResponse): Partial<NutSheetRowDraft> {
  return {
    // Canonical DealerBuilt echo (normalizes leading zeros/whitespace).
    dealNumber: result.dealNumber,
    ...(result.stockNumber != null ? { stockNumber: result.stockNumber } : {}),
    soldYear: result.soldYear,
    soldMake: result.soldMake,
    soldModel: result.soldModel,
    salesperson: result.salesperson,
    salesperson2: result.salesperson2,
    salesManager: result.salesManager,
    fiRep: result.fiRep,
    customerLastName: result.customerLastName,
    daysInStock: result.daysInStock,
    openROs: result.openROs,
    financeSource: result.financeSource,
    financeSourceName: result.financeSourceName,
    financeReserve: result.financeReserve,
    tradeStockNumber: result.tradeStockNumber,
    tradeYear: result.tradeYear,
    tradeMake: result.tradeMake,
    tradeModel: result.tradeModel,
    tradeMiles: result.tradeMiles,
    trade2StockNumber: result.trade2StockNumber,
    trade2Year: result.trade2Year,
    trade2Make: result.trade2Make,
    trade2Model: result.trade2Model,
    trade2Miles: result.trade2Miles,
  };
}

interface NutSheetRowProps {
  row: NutSheetRowDraft;
  locationId: number;
  onChange: (patch: Partial<NutSheetRowDraft>) => void;
  onCommit: () => void;
  onDelete: () => void;
  onFocusRow: () => void;
  onBlurRow: () => void;
  onResolveConflict: (action: 'reload' | 'keepMine') => void;
  onResolveDeleted: (action: 'recreate' | 'discard') => void;
  onShowHistory: () => void;
  /** Fires when the entered deal # already exists on this location's sheet. */
  onDuplicateDeal: (result: DealLookupResponse, matches: ExistingNutSheetMatch[]) => void;
  saving: boolean;
  /** When true, the F&I cell shows fAndI − financeReserve as a read-only value. */
  excludeFiReserve: boolean;
  /** Accounting/Admin get the editable "+ Comment" affordance under Actual Gross;
   *  everyone else sees the note read-only (or nothing when empty). */
  canEditAccountingComment: boolean;
}

const TRADE_BG = 'bg-amber-50';

// All cells top-align so the tops of the input boxes line up across the
// row even when the Deal/Trade blocks grow taller than the inputs.
const TD = 'px-1 py-1 align-top';

export const NutSheetRow: React.FC<NutSheetRowProps> = ({
  row,
  locationId,
  onChange,
  onCommit,
  onDelete,
  onFocusRow,
  onBlurRow,
  onResolveConflict,
  onResolveDeleted,
  onShowHistory,
  onDuplicateDeal,
  saving,
  excludeFiReserve,
  canEditAccountingComment,
}) => {
  // Whether the writer is actively typing the accounting note (shows a textarea);
  // otherwise the note collapses to clickable text / a "+ Comment" affordance.
  const [editingComment, setEditingComment] = useState(false);

  // Commit on any blur within the row (existing behavior). Additionally clear
  // the focused-row marker only when focus actually leaves the row, so the
  // background poll can resume refreshing it.
  const handleRowBlur = (e: React.FocusEvent<HTMLTableRowElement>) => {
    onCommit();
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      onBlurRow();
    }
  };

  const handleText = (field: keyof NutSheetRowDraft) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ [field]: e.target.value } as Partial<NutSheetRowDraft>);
    };

  const onTypeChange = (code: string, category: NutSheetCategory) => {
    onChange({ type: code, category });
  };

  const onDealLookup = (result: DealLookupResponse) => {
    // If this deal # already exists on the sheet (excluding this very row),
    // warn before doing anything else — hold the auto-fill until the operator
    // confirms via the page-level modal (which replays dealAutofillPatch).
    const dupes = (result.existingEntries ?? []).filter((m) => m.entryId !== row.entryId);
    if (dupes.length > 0) {
      onDuplicateDeal(result, dupes);
      return;
    }
    if (!result.found) return;
    onChange(dealAutofillPatch(result));
  };

  const flagged = row.conflict || row.deletedRemotely;

  // Actual Gross border flags delivery vs. estimate: red when it comes in under
  // Est. Gross, green when over, neutral when equal/blank.
  const actualVsEst =
    row.actualGross == null ||
    row.estimatedGross == null ||
    row.actualGross === row.estimatedGross
      ? 'border-gray-500'
      : row.actualGross < row.estimatedGross
      ? 'border-red-500 ring-1 ring-red-300'
      : 'border-green-600 ring-1 ring-green-300';
  const actualGrossClass = `${cellBase} ${actualVsEst} text-right w-24`;

  return (
    <>
    <tr
      className={`border-t border-gray-300 ${flagged ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
      onFocus={onFocusRow}
      onBlur={handleRowBlur}
    >
      {/* Type */}
      <td className={`${TD} w-20`}>
        <TypeSelect
          locationId={locationId}
          value={row.type}
          onChange={onTypeChange}
          disabled={saving}
        />
      </td>
      {/* Deal # (drives the DealerBuilt lookup) + Stock # beneath (auto-filled
          from the deal; stays editable as a manual fallback — typing it does
          NOT fire a lookup). */}
      <td className={TD}>
        <DealNumberInput
          locationId={locationId}
          date={row.date}
          value={row.dealNumber ?? ''}
          onChange={(v) => onChange({ dealNumber: v })}
          onLookupResult={onDealLookup}
          disabled={saving}
        />
        <input
          type="text"
          value={row.stockNumber}
          onChange={(e) => onChange({ stockNumber: e.target.value })}
          disabled={saving}
          placeholder="Stock #"
          title="Auto-filled from the Deal #; editable if the deal isn't in DealerBuilt yet"
          className={`${textCell} mt-0.5 w-24 disabled:opacity-60`}
        />
      </td>
      {/* Deal Info (auto-populated from the deal-number lookup, click to edit) */}
      <td className={TD}>
        <AutoPopBlock
          row={row}
          onChange={onChange}
          onCommit={onCommit}
          saving={saving}
        />
      </td>
      {/* Deal financials: Est. Gross / Actual Gross / F&I / Back End $ / Holdback / Doc / Finance Source
          (text/number/currency inputs stay enabled during save — disabling
          them would yank them out of the tab order mid-keystroke and break
          the Tab-across-the-row flow.) */}
      <td className={TD}>
        <CurrencyInput
          value={row.estimatedGross}
          onChange={(v) => {
            const patch: Partial<NutSheetRowDraft> = { estimatedGross: v };
            // Mirror Est. Gross into Actual Gross while the two are still in sync
            // (Actual blank or equal to the prior Est) — once the operator
            // overrides Actual, stop auto-filling it.
            if (row.actualGross == null || row.actualGross === row.estimatedGross) {
              patch.actualGross = v;
            }
            onChange(patch);
          }}
          className={`${numCell} w-24`}
        />
      </td>
      <td className={TD}>
        <CurrencyInput value={row.actualGross} onChange={(v) => onChange({ actualGross: v })} className={actualGrossClass} />
        {/* Accounting note, anchored under Actual Gross (same stacked-cell pattern
            as the F&I net hint above). Writable by Accounting/Admin only; every
            other role sees the text read-only, or nothing when empty. */}
        {canEditAccountingComment ? (
          editingComment ? (
            <textarea
              autoFocus
              rows={2}
              maxLength={ACCOUNTING_COMMENT_MAX}
              value={row.accountingComment ?? ''}
              onChange={(e) => onChange({ accountingComment: e.target.value })}
              onBlur={() => setEditingComment(false)}
              placeholder="Accounting note…"
              className={`${textCell} mt-0.5 w-24 text-[10px] leading-tight resize-none`}
            />
          ) : row.accountingComment?.trim() ? (
            <div
              className="mt-0.5 w-24 text-[10px] leading-tight italic text-purple-800 break-words cursor-pointer hover:underline"
              title="Click to edit accounting note"
              onClick={() => setEditingComment(true)}
            >
              {row.accountingComment}
            </div>
          ) : (
            // persistRow no-ops on rows without a Type, so a note typed on a
            // blank row can't save — disable the affordance until a Type is set.
            <button
              type="button"
              disabled={!row.type}
              onClick={() => setEditingComment(true)}
              title={row.type ? 'Add an accounting note' : 'Select a Type first'}
              className="mt-0.5 text-[10px] text-blue-600 hover:underline disabled:text-gray-300 disabled:cursor-not-allowed disabled:no-underline"
            >
              + Comment
            </button>
          )
        ) : (
          row.accountingComment?.trim() && (
            <div className="mt-0.5 w-24 text-[10px] leading-tight italic text-purple-800 break-words">
              {row.accountingComment}
            </div>
          )
        )}
      </td>
      <td className={TD}>
        {/* The F&I input is ALWAYS editable — the operator must be able to enter
            the true full F&I regardless of the Exclude-F&I-Reserve toggle. When
            excluding, the net (F&I − reserve) shows as a read-only hint beneath
            it; it never replaces or overrides the editable value. */}
        <CurrencyInput value={row.fAndI} onChange={(v) => onChange({ fAndI: v })} className={`${numCell} w-24`} />
        {excludeFiReserve && row.financeReserve != null && row.fAndI != null && (
          <div
            className="mt-0.5 text-xs font-medium text-right text-gray-700 tabular-nums"
            title={`F&I ${formatCurrency(row.fAndI)} − reserve ${formatCurrency(row.financeReserve)}`}
          >
            Prod. {formatCurrency(row.fAndI - row.financeReserve)}
          </div>
        )}
      </td>
      <td className={TD}>
        <CurrencyInput value={row.points} onChange={(v) => onChange({ points: v })} className={`${numCell} w-24`} />
      </td>
      <td className={TD}>
        <CurrencyInput value={row.holdback} onChange={(v) => onChange({ holdback: v })} className={`${numCell} w-24`} />
      </td>
      <td className={TD}>
        <CurrencyInput value={row.doc} onChange={(v) => onChange({ doc: v })} className={`${numCell} w-20`} />
      </td>
      <td className={TD}>
        <input
          type="text"
          value={row.financeSource ?? ''}
          onChange={handleText('financeSource')}
          title={row.financeSourceName ?? ''}
          className={`${textCell} w-24`}
        />
      </td>
      {/* Trade Info (auto-populated from the deal-number lookup; up to 2 trades, click to edit) */}
      <td className={`${TD} ${TRADE_BG}`}>
        <TradeInfoBlock
          row={row}
          onChange={onChange}
          onCommit={onCommit}
          saving={saving}
        />
      </td>
      {/* Per-trade financials (ACV / Retail / Plan) — operator-entered, stacked
          T1/T2 sub-rows in a single amber cell. */}
      <td className={`${TD} ${TRADE_BG}`}>
        <TradeFinancialsBlock
          row={row}
          locationId={locationId}
          onChange={onChange}
          saving={saving}
        />
      </td>
      <td className={`${TD} pt-1.5`}>
        <div className="flex flex-col items-center gap-1.5">
          <button
            onClick={onShowHistory}
            disabled={!row.entryId}
            title={row.entryId ? 'Change history' : 'Save the row to view history'}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ClockIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            disabled={saving}
            title="Delete row"
            className="text-rose-600 hover:text-rose-800 disabled:opacity-40"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
    {flagged && (
      <tr className="bg-amber-50">
        <td colSpan={13} className="px-3 py-1.5 border-t border-amber-200">
          {row.deletedRemotely ? (
            <div className="flex items-center gap-3 text-xs text-amber-900">
              <span>This row was deleted by another user while you were editing it.</span>
              <button
                onClick={() => onResolveDeleted('recreate')}
                className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700"
              >
                Re-add my version
              </button>
              <button
                onClick={() => onResolveDeleted('discard')}
                className="px-2 py-0.5 rounded border border-amber-400 text-amber-800 hover:bg-amber-100"
              >
                Discard
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-xs text-amber-900">
              <span>
                Updated by {row.conflictBy || 'another user'} since you started editing.
              </span>
              <button
                onClick={() => onResolveConflict('reload')}
                className="px-2 py-0.5 rounded border border-amber-400 text-amber-800 hover:bg-amber-100"
              >
                Reload theirs
              </button>
              <button
                onClick={() => onResolveConflict('keepMine')}
                className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700"
              >
                Keep mine
              </button>
            </div>
          )}
        </td>
      </tr>
    )}
    </>
  );
};
